// src/components/sidebarcart/CustomProductModal.js
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    InputAdornment,
} from '@mui/material';
import { IoCloseCircleOutline, IoCheckmarkCircleOutline } from 'react-icons/io5';

// Common IVA options in Argentina
const IVA_OPTIONS = [
    { value: 0, label: '0%' },
    { value: 10.5, label: '10.5%' },
    { value: 21, label: '21%' },
    { value: 27, label: '27%' },
];

function CustomProductModal({ open, onClose, onAdd }) {
    const [description, setDescription] = useState('');
    const [price, setPrice] = useState('');
    const [quantity, setQuantity] = useState('1');
    const [iva, setIva] = useState(0); // Default 0%
    const [errors, setErrors] = useState({});

    // Reset form when modal closes
    const handleClose = useCallback(() => {
        setDescription('');
        setPrice('');
        setQuantity('1');
        setIva(0);
        setErrors({});
        onClose();
    }, [onClose]);

    // Validate form
    const validate = useCallback(() => {
        const newErrors = {};

        if (!description.trim()) {
            newErrors.description = 'La descripción es requerida';
        } else if (description.trim().length < 3) {
            newErrors.description = 'La descripción debe tener al menos 3 caracteres';
        }

        const priceNum = parseFloat(price);
        if (!price || isNaN(priceNum) || priceNum <= 0) {
            newErrors.price = 'El precio debe ser mayor a 0';
        }

        const quantityNum = parseFloat(quantity);
        if (!quantity || isNaN(quantityNum) || quantityNum <= 0) {
            newErrors.quantity = 'La cantidad debe ser mayor a 0';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [description, price, quantity]);

    // Handle add button click
    const handleAdd = useCallback(() => {
        if (validate()) {
            const customProduct = {
                description: description.trim(),
                price: parseFloat(price),
                quantity: parseFloat(quantity),
                iva: iva,
            };

            onAdd(customProduct);
            handleClose();
        }
    }, [description, price, quantity, iva, validate, onAdd, handleClose]);

    // Handle Enter key press
    const handleKeyPress = useCallback((e) => {
        if (e.key === 'Enter') {
            handleAdd();
        }
    }, [handleAdd]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={false}
            fullWidth={false}
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    padding: '8px',
                    position: 'fixed',
                    right: '50px',
                    top: '100px',
                    margin: 0,
                    width: '891px', // 10% less than drawer width (990px * 0.9)
                    zIndex: 1301, // Above drawer (drawer is typically 1300)
                },
            }}
            sx={{
                '& .MuiBackdrop-root': {
                    backgroundColor: 'rgba(0, 0, 0, 0.3)', // Light backdrop
                },
            }}
        >
            <DialogTitle sx={{ fontWeight: 600, fontSize: '1.3rem', pb: 1 }}>
                Agregar Producto Personalizado
            </DialogTitle>

            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Complete los datos del producto personalizado. Estos productos no afectarán el stock.
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {/* Description Field */}
                    <TextField
                        label="Descripción del Producto"
                        placeholder="Ej: Producto Fuera de Listas"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        onKeyPress={handleKeyPress}
                        error={!!errors.description}
                        helperText={errors.description || 'Nombre descriptivo del producto'}
                        fullWidth
                        required
                        autoFocus
                        multiline
                        rows={2}
                        inputProps={{ maxLength: 500 }}
                    />

                    {/* Price and Quantity Row */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {/* Price Field */}
                        <TextField
                            label="Precio Unitario"
                            type="number"
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            onKeyPress={handleKeyPress}
                            error={!!errors.price}
                            helperText={errors.price}
                            required
                            sx={{ flex: 1 }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                inputProps: { 
                                    min: 0, 
                                    step: 0.01,
                                    style: { textAlign: 'right' }
                                }
                            }}
                        />

                        {/* Quantity Field */}
                        <TextField
                            label="Cantidad"
                            type="number"
                            value={quantity}
                            onChange={(e) => setQuantity(e.target.value)}
                            onKeyPress={handleKeyPress}
                            error={!!errors.quantity}
                            helperText={errors.quantity}
                            required
                            sx={{ flex: 1 }}
                            InputProps={{
                                inputProps: { 
                                    min: 0.01, 
                                    step: 0.01,
                                    style: { textAlign: 'center' }
                                }
                            }}
                        />
                    </Box>

                    {/* IVA Selector */}
                    <FormControl fullWidth>
                        <InputLabel id="iva-label">IVA</InputLabel>
                        <Select
                            labelId="iva-label"
                            value={iva}
                            label="IVA"
                            onChange={(e) => setIva(e.target.value)}
                        >
                            {IVA_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Preview Calculation */}
                    {price && quantity && (
                        <Box
                            sx={{
                                backgroundColor: 'rgba(71, 117, 186, 0.1)',
                                borderRadius: '8px',
                                padding: '12px 16px',
                                borderLeft: '4px solid #4775ba',
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                Total estimado (con IVA):
                            </Typography>
                            <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                                $ {((parseFloat(price) || 0) * (parseFloat(quantity) || 0) * (1 + iva / 100)).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ padding: '16px 24px' }}>
                <Button
                    onClick={handleClose}
                    startIcon={<IoCloseCircleOutline size={20} />}
                    sx={{
                        color: 'text.secondary',
                        textTransform: 'none',
                        fontWeight: 500,
                    }}
                >
                    Cancelar
                </Button>
                <Button
                    onClick={handleAdd}
                    variant="contained"
                    startIcon={<IoCheckmarkCircleOutline size={20} />}
                    sx={{
                        backgroundColor: '#4775ba',
                        textTransform: 'none',
                        fontWeight: 600,
                        paddingX: 3,
                        '&:hover': {
                            backgroundColor: '#3a5f9a',
                        },
                    }}
                >
                    Agregar al Carrito
                </Button>
            </DialogActions>
        </Dialog>
    );
}

CustomProductModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onAdd: PropTypes.func.isRequired,
};

export default CustomProductModal;
